import { ErrorBoundary } from '@/components/error-boundary';
import { InertiaAppProviders, syncLocaleFromPageProps } from '@/components/inertia-app-providers';
import { initializeTheme } from '@/hooks/use-appearance';
import type { InertiaEntry } from '@/lib/app-entry';
import { assignWindowZiggy, readInitialInertiaPage } from '@/lib/browser-ziggy';
import { initConsentTracking } from '@/lib/consent';
import { afterWindowLoad, onFirstUserInteraction, whenBrowserIdle, whenUsercentricsReady } from '@/lib/deferred-runtime';
import { ensureDataLayer, loadGTM, persistPendingGTMEvents, pushServerTrackingEvents, trackPageView } from '@/lib/gtm';
import i18n from '@/lib/i18n';
import { loadPageTranslations } from '@/lib/i18n-loaders';
import { localeFromPageProps } from '@/lib/i18n-page-map';
import { mountInertiaApp } from '@/lib/mount-inertia-app';
import { initSentry, listenForEarlyErrors, setSentryAuthenticated } from '@/lib/sentry';
import { listenForStaleChunkReload, withStaleChunkRecovery } from '@/lib/stale-chunk-reload';
import ErrorFallback from '@/pages/error-fallback';
import { formatDocumentTitle } from '@/utils/seo-helpers';
import type { Page } from '@inertiajs/core';
import { createInertiaApp, router, type ResolvedComponent } from '@inertiajs/react';
import { setNonce } from 'get-nonce';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import type { ReactElement, ReactNode } from 'react';
import { toast } from 'sonner';

type PageGlob = Record<string, () => Promise<unknown>>;

export function createBrowserInertiaApp({
    pages,
    entry,
    wrapPage = (page) => page,
}: {
    pages: PageGlob;
    entry: InertiaEntry;
    wrapPage?: (page: ReactElement) => ReactNode;
}): void {
    const cspNonce: string | undefined = document.querySelector<HTMLMetaElement>('meta[name="csp-nonce"]')?.content;

    if (cspNonce) {
        setNonce(cspNonce);
    }

    listenForEarlyErrors();
    listenForStaleChunkReload();
    initializeTheme();

    const gtmConfig = {
        id: import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID || '',
        serverUrl: import.meta.env.VITE_GOOGLE_TAG_MANAGER_SERVER_URL || 'https://s.heykaution.de',
        enabled: import.meta.env.VITE_GOOGLE_TAG_MANAGER_ENABLED === 'true' && !!import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID,
        nonce: cspNonce,
    };

    const initialPageData = readInitialInertiaPage();
    const initialUserId = initialPageData.props?.auth?.user?.id ?? null;

    assignWindowZiggy(initialPageData.props?.ziggy);
    setSentryAuthenticated(Boolean(initialUserId));
    whenBrowserIdle(() => void initSentry());
    let stopWaitingForInteraction: () => void = () => undefined;
    let gtmRequested = false;
    const startGTM = () => {
        if (gtmRequested) {
            return;
        }

        gtmRequested = true;
        stopWaitingForInteraction();
        whenUsercentricsReady(() => {
            loadGTM({ ...gtmConfig, userId: initialUserId });
        });
    };
    stopWaitingForInteraction = onFirstUserInteraction(startGTM);
    afterWindowLoad(startGTM);

    function localeFromPage(page?: Page): string {
        return localeFromPageProps(page) ?? initialPageData.props?.locale ?? i18n.language;
    }

    createInertiaApp({
        title: (title) => formatDocumentTitle(title),
        resolve: (name, page) =>
            withStaleChunkRecovery(async () => {
                await loadPageTranslations(localeFromPage(page), name);

                return resolvePageComponent(`./pages/${name}.tsx`, pages) as Promise<ResolvedComponent>;
            }),
        setup({ el, App, props }) {
            syncLocaleFromPageProps(props);

            mountInertiaApp(
                el,
                <InertiaAppProviders entry={entry}>
                    <ErrorBoundary fallback={(error, reset) => <ErrorFallback error={error} reset={reset} />}>
                        {wrapPage(<App {...props} />)}
                    </ErrorBoundary>
                </InertiaAppProviders>,
            );
        },
        progress: {
            color: '#4B5563',
        },
    });

    ensureDataLayer(initialUserId);
    pushServerTrackingEvents(initialPageData.props?.trackingEvents);
    initConsentTracking();

    router.on('httpException', (event) => {
        if (event.detail.response.status === 429) {
            event.preventDefault();
            toast.error(i18n.t('support.errors.tooManyRequests'));
        }
    });

    // Keep global Ziggy config in sync on Inertia navigation.
    // Without this, the global route() function uses stale defaults
    // (e.g. wrong locale) after client-side navigations.
    router.on('navigate', (event) => {
        assignWindowZiggy(event.detail.page.props.ziggy);
        pushServerTrackingEvents(event.detail.page.props.trackingEvents);
        const auth = event.detail.page.props.auth as { user?: { id?: string } | null } | undefined;
        setSentryAuthenticated(Boolean(auth?.user?.id));
    });

    if (gtmConfig.enabled) {
        window.addEventListener('pagehide', persistPendingGTMEvents, { capture: true });
        trackPageView(window.location.href, document.title);

        let previousUserId: string | null = initialUserId;

        router.on('navigate', (event) => {
            const auth = event.detail.page.props.auth as { user?: { id?: string } | null } | undefined;
            const currentUserId = auth?.user?.id ?? null;

            if (currentUserId !== previousUserId) {
                window.dataLayer.push({ user_id: currentUserId });
                previousUserId = currentUserId;
            }

            trackPageView(event.detail.page.url, (event.detail.page.props.title as string) || document.title);
        });
    }
}
